import { GetProgrammaticCampaignVideoCompletionRateRO } from "@intentsify/dto";
import { isPopulatedArray } from "@intentsify/utils";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  MIN_MODAL_CONTENT_HEIGHT,
  VideoCompletionRateChart,
} from "components";
import { DisplayPerformanceErrorBoundary } from "../../../../DisplayPerformanceErrorBoundary";
import { useMemo, useState } from "react";
import { MdOutlineTvOff } from "react-icons/md";
import { DisplayPerformanceFilters } from "../../../../types";
import { ChartExport, useExportableChart } from "shared/components";

type VideoCompletionRateProps = {
  campaignId: string;
  filters: DisplayPerformanceFilters;
  getVideoCompletionRate: (
    id: string,
    filters: DisplayPerformanceFilters
  ) => Promise<GetProgrammaticCampaignVideoCompletionRateRO>;
};

const TITLE = "Average % Watched";

const VideoCompletionRate = (props: VideoCompletionRateProps) => (
  <DisplayPerformanceErrorBoundary cardTitle={TITLE}>
    <VideoCompletionRateContent {...props} />
  </DisplayPerformanceErrorBoundary>
);

const VideoCompletionRateContent = ({
  campaignId,
  filters,
  getVideoCompletionRate,
}: VideoCompletionRateProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { data, isFetching } = useQuery(
    ["videoCompletionRate", campaignId, filters],
    () => getVideoCompletionRate(campaignId, filters)
  );

  const normalisedData = useMemo(() => {
    return (
      data?.videoCompletionRate.map((v) => ({
        label: v.creativeName,
        value: v.completionRatePct * 100,
      })) ?? []
    );
  }, [data?.videoCompletionRate]);

  const chart = useExportableChart({
    title: TITLE,
    campaignId: Number(campaignId),
    chart: (
      <VideoCompletionRateChart
        minHeight={isExpanded ? MIN_MODAL_CONTENT_HEIGHT : undefined}
        data={normalisedData}
      />
    ),
  });

  return (
    <Card
      onExpandedChange={setIsExpanded}
      hasData={isPopulatedArray(data?.videoCompletionRate)}
      noDataIcon={MdOutlineTvOff}
      noDataMessage="This visualization is designated for campaigns with allocated video formats and creatives."
      isLoading={isFetching}
      title={TITLE}
      isExpandable
      actions={
        <ChartExport
          size="small"
          campaignId={Number(campaignId)}
          title={chart.title}
          onExportPNG={chart.downloadAsPng}
          data={normalisedData}
        />
      }
    >
      {chart.component}
      {chart.exportComponent}
    </Card>
  );
};

export { VideoCompletionRate };
